<template>
  <ListItem :to="to">
    <div class="flex flex-col items-start gap-1">
      <span
        v-if="
          appointment.allocations[0]?.status === AllocationStatus.HANDED_OVER &&
          appointment.readyForPickup
        "
        class="rounded bg-walnut-50 px-2 py-0.5 text-xs font-medium text-walnut-700"
      >
        {{ t('readyForPickup') }}
      </span>
      <AllocationStatusBadge
        v-else
        :status="appointment.allocations[0]?.status ?? AllocationStatus.NOT_CONFIRMED"
      />
      <Badge v-if="appointment.allocations[0]?.isBlocking === false" variant="warning">
        {{ t('doesNotBlockBookings') }}
      </Badge>
    </div>
    <div class="min-w-0 flex-1 self-center text-sm">
      <p class="font-semibold">{{ typesLabel }}</p>
      <p class="text-hazel-700">{{ locationLabel }}</p>
    </div>
    <div class="justify-self-start text-sm font-medium md:justify-self-end">
      {{ formatCurrency(appointment.totalCosts ?? 0) }}
    </div>
    <div
      class="grid grid-cols-[1rem_auto] grid-rows-2 items-center gap-x-1 gap-y-0.5 justify-self-end text-right text-sm max-sm:justify-self-start max-sm:text-left"
    >
      <ArrowRightFromLineIcon class="h-4 w-4 text-hazel-500" />
      <span>{{
        formatDate(appointment.allocations[0]?.startDate ?? null, 'dd.MM.yyyy - HH:mm')
      }}</span>
      <ArrowLeftToLineIcon class="h-4 w-4 text-hazel-500" />
      <span>{{
        formatDate(appointment.allocations[0]?.endDate ?? null, 'dd.MM.yyyy - HH:mm')
      }}</span>
    </div>
  </ListItem>
</template>

<script lang="ts" setup>
import AllocationStatusBadge from '@/components/badges/AllocationStatusBadge.vue';
import Badge from '@/components/Badge.vue';
import ListItem from '@/components/ListItem.vue';
import { BookingLocationType } from '@/entities/bookings/booking-location-type.enum';
import type { WorkshopAppointment } from '@/entities/workshop-appointments/workshop-appointment.entity';
import { formatDate } from '@/hooks/use-formatted-date';
import { useLocalizedFields } from '@/hooks/use-localized-fields';
import { formatCurrency } from '@/utils/format-numbers';
import { AllocationStatus } from '@carvia/ros-client-types';
import { ArrowLeftToLineIcon, ArrowRightFromLineIcon } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = withDefaults(
  defineProps<{
    appointment: WorkshopAppointment;
    clickable?: boolean;
  }>(),
  {
    clickable: true,
  },
);

const { t } = useI18n();
const { getLocalizedField } = useLocalizedFields();

const to = computed(() =>
  props.clickable
    ? { name: 'editWorkshopAppointment', params: { id: props.appointment.id } }
    : undefined,
);

const typesLabel = computed(() =>
  props.appointment.types.map((type) => t(`workshopAppointmentTypes.${type}`)).join(', '),
);

const locationLabel = computed(() => {
  const allocation = props.appointment.allocations[0];
  if (!allocation) return '—';
  if (allocation.pickupLocationType === BookingLocationType.STATION) {
    return getLocalizedField(allocation.pickupResponsibleStation?.info) || '—';
  }
  return allocation.pickupLocationGeocodedAddress?.displayName ?? '—';
});
</script>

<i18n lang="json">
{
  "en": {
    "readyForPickup": "Ready for pickup",
    "doesNotBlockBookings": "Does not block bookings"
  },
  "de": {
    "readyForPickup": "Abholbereit",
    "doesNotBlockBookings": "Blockiert keine Buchungen"
  }
}
</i18n>
